import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject, Observable } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { HubConnectionState } from '@microsoft/signalr';
import { environment } from '../../../../environments/environment';
import { StorageService } from '../api/storage.service';
import { ApiService } from '../api/api.service';
import { StorageKeys } from 'enums/storage-keys.enum';
import { SignalRService } from 'app/main/shared/services/signalr.service';
import { AuthService } from '../auth/auth.service';

type CallRole = 'caller' | 'callee';

export interface IncomingCallEvent {
  callId: string;
  fromUserId: string;
  fromUserName: string;
  callType: any;
  startedAtUtc: string;
}

export interface CallEndedEvent {
  callId: string;
  endedByUserId: string;
}

export interface CallRejectedEvent {
  callId: string;
  byUserId?: string;
  reason?: string;
}

export interface VideoStatusEvent {
  callId: string;
  fromUserId: string;
  isVideoOff: boolean;
}

export interface CallErrorEvent {
  errorCode: string;
  message?: string;
}

interface SdpOffer {
  callId: string;
  sdp: string;
  type: 'offer';
}

interface SdpAnswer {
  callId: string;
  sdp: string;
  type: 'answer';
}

interface IceCandidate {
  callId: string;
  candidate: string;
  sdpMid: string;
  sdpMLineIndex: number;
}

@Injectable({ providedIn: 'root' })
export class CallHubService implements OnDestroy {
  private readonly hubName = environment.hubs.callHub.name;
  private readonly hubUrl = environment.hubs.callHub.url;

  private pc?: RTCPeerConnection;
  private localStream?: MediaStream;

  private readonly localStream$ = new BehaviorSubject<MediaStream | null>(null);
  private readonly remoteStream$ = new BehaviorSubject<MediaStream | null>(
    null
  );

  private currentCallId?: string;
  private perCallTeardown$?: Subject<void>;
  private destroy$ = new Subject<void>();
  private currentRole?: CallRole;

  private callEnded$ = new Subject<CallEndedEvent>();

  private videoStatusChanged$ = new Subject<VideoStatusEvent>();

  private outgoingCallTimeoutTimer?: any;
  private readonly outgoingCallTimeoutMs = 15000;

  private callState$ = new BehaviorSubject<{
    inCall: boolean;
    callId?: string;
    role?: CallRole;
  }>({
    inCall: false,
  });

  public getCallState(): Observable<{
    inCall: boolean;
    callId?: string;
    role?: CallRole;
  }> {
    return this.callState$.asObservable();
  }

  public onVideoStatusChanged(): Observable<VideoStatusEvent> {
    return this.videoStatusChanged$.asObservable();
  }

  onCallEnded() {
    return this.callEnded$.asObservable();
  }

  private callRejected$ = new Subject<CallRejectedEvent>();
  onCallRejected(): Observable<CallRejectedEvent> {
    return this.callRejected$.asObservable();
  }

  private incomingCall$ = new Subject<IncomingCallEvent>();

  onIncomingCall(): Observable<IncomingCallEvent> {
    return this.incomingCall$.asObservable();
  }

  private callError$ = new Subject<CallErrorEvent>();
  onCallError(): Observable<CallErrorEvent> {
    return this.callError$.asObservable();
  }

  private globalHandlersBound = false;
  private pendingOffers = new Map<string, { sdp: string }>();
  private pendingCandidates = new Map<string, RTCIceCandidateInit[]>();
  private pendingAnswers = new Map<string, { sdp: string }>();

  private candSent = 0;
  private candRecv = 0;

  private iceServersLoaded = false;
  private dynamicIceServers?: RTCIceServer[];

  private remoteDisconnectTimer?: any;
  private readonly remoteDisconnectGraceMs =
    environment.webrtc?.disconnectedGraceMs ?? 4000;

  private recentIncoming = new Map<string, number>();
  private readonly DUPLICATE_INCOMING_MS = 2000;

  constructor(
    private signalR: SignalRService,
    private storage: StorageService,
    private apiService: ApiService,
    private authService: AuthService
  ) {
    this.authService.onLogout$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cleanupPeer();
    });

    this.signalR.onDisconnected(this.hubName, () => {
      this.globalHandlersBound = false;
    });

    this.signalR.onReconnected(this.hubName, () => {
      if (!this.globalHandlersBound) {
        this.bindGlobalSignaling();
      }
    });
  }

  public async ensureSignaling(): Promise<void> {
    await this.ensureConnected();
  }

  getLocalStream$() {
    return this.localStream$.asObservable();
  }

  getRemoteStream$() {
    return this.remoteStream$.asObservable();
  }

  private async ensureConnected(): Promise<void> {
    const hubState = this.signalR.getHubState(this.hubName);
    if (hubState === HubConnectionState.Connected) {
      if (!this.globalHandlersBound) {
        this.bindGlobalSignaling();
      }
      return;
    }

    const user = this.storage.get(StorageKeys.USER);
    if (!user?.id) throw new Error('User not found in storage.');
    const params = new URLSearchParams();
    params.set('userId', user.id);
    
    await this.signalR.connect(
      this.hubName,
      this.hubUrl,
      params,
      'video-chat-service'
    );
    this.bindGlobalSignaling();
  }

  private bindGlobalSignaling() {
    if (this.globalHandlersBound) return;
    this.globalHandlersBound = true;

    this.signalR
      .on<any>(this.hubName, 'IncomingCall')
      .pipe(takeUntil(this.destroy$))
      .subscribe((ev: any) => {
        try {
          const callId = ev?.callId ?? ev?.CallId;
          if (!callId) return;

          const now = Date.now();
          const last = this.recentIncoming.get(callId);
          if (last && now - last < this.DUPLICATE_INCOMING_MS) {
            this.log('Ignored duplicate IncomingCall', {
              callId,
              sinceMs: now - last,
            });
            return;
          }
          this.recentIncoming.set(callId, now);
          setTimeout(() => {
            try {
              this.recentIncoming.delete(callId);
            } catch {}
          }, this.DUPLICATE_INCOMING_MS + 500);

          const incoming: IncomingCallEvent = {
            callId,
            fromUserId: ev?.fromUserId ?? ev?.FromUserId ?? '',
            fromUserName: ev?.fromUserName ?? ev?.FromUserName ?? '',
            callType: ev?.callType ?? ev?.CallType ?? 2,
            startedAtUtc:
              ev?.startedAtUtc ?? ev?.StartedAtUtc ?? new Date().toISOString(),
          };
          this.incomingCall$.next(incoming);
        } catch (e) {
          console.log('IncomingCall handler error', e);
        }
      });

    this.signalR.on<any>(this.hubName, 'ReceiveOffer').subscribe((off) => {
      const callId = off.callId ?? off.CallId;
      const sdp = off.sdp ?? off.Sdp;
      if (!callId || !sdp) return;
      this.pendingOffers.set(callId, { sdp });
    });

    this.signalR.on<any>(this.hubName, 'ReceiveIceCandidate').subscribe((c) => {
      const callId = c.callId ?? c.CallId;
      if (!callId) return;
      const list = this.pendingCandidates.get(callId) ?? [];
      list.push({
        candidate: c.candidate ?? c.Candidate,
        sdpMid: c.sdpMid ?? c.SdpMid,
        sdpMLineIndex: c.sdpMLineIndex ?? c.SdpMLineIndex,
      });
      this.pendingCandidates.set(callId, list);
    });

    this.signalR.on<any>(this.hubName, 'CallEnded').subscribe((ev: any) => {
      this.clearOutgoingCallTimeout();
      const callId = ev?.callId ?? ev?.CallId;
      const endedByUserId = ev?.endedByUserId ?? ev?.EndedByUserId;
      if (!callId) return;
      this.callEnded$.next({ callId, endedByUserId });
      if (this.currentCallId && callId === this.currentCallId) {
        this.cleanupPeer();
        this.callState$.next({
          inCall: false,
        });
      }
      if (ev?.reason === 'Rejected') {
        this.callRejected$.next({
          callId: ev?.callId,
          byUserId: ev?.byUserId,
          reason: 'Rejected',
        });
      }
    });

    this.signalR
      .on<any>(this.hubName, 'CallRejected')
      .subscribe((payload: any) => {
        try {
          this.clearOutgoingCallTimeout();
          const callId = payload?.callId ?? payload?.CallId;
          this.callRejected$.next({
            callId,
            byUserId: payload?.byUserId,
            reason: 'Rejected',
          });

          if (callId && this.currentCallId && callId === this.currentCallId) {
            try {
              this.cleanupPeer();
            } catch (err) {
              this.log('cleanupPeer on CallRejected failed', err);
            }

            try {
              this.callEnded$.next({
                callId,
                endedByUserId: payload?.byUserId ?? '',
              });
            } catch {}
          }
        } catch (e) {
          console.warn('CallRejected handler error', e);
        }
      });

    this.signalR
      .on<any>(this.hubName, 'ReceiveAnswer')
      .subscribe((ans: any) => {
        const callId = ans?.callId ?? ans?.CallId;
        const sdp = ans?.sdp ?? ans?.Sdp;
        if (!callId || !sdp) return;
        this.pendingAnswers.set(callId, { sdp });
        this.log('Buffered Answer', { callId });
      });

    // CalleeReady - Callee hazır olduğunda offer gönder
    this.signalR
      .on<any>(this.hubName, 'CalleeReady')
      .subscribe(async (event: any) => {
        const callId = event?.callId ?? event?.CallId;
        if (!callId || callId !== this.currentCallId) {
          this.log('CalleeReady: callId mismatch or no current call');
          return;
        }
        if (!this.pc) {
          return;
        }

        try {
          const offer = await this.pc.createOffer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: true,
          });

          await this.pc.setLocalDescription(offer);

          await this.signalR.invoke(this.hubName, 'SendOffer', {
            CallId: callId,
            Sdp: offer.sdp,
            Type: 'offer',
          });
          this.scheduleAnswerWatch(callId);
        } catch (error) {
          this.log('Error creating/sending offer:', error);
        }
      });

    this.signalR
      .on<any>(this.hubName, 'VideoStatusChanged')
      .subscribe((data) => {
        this.videoStatusChanged$.next({
          callId: data.callId ?? data.CallId,
          fromUserId: data.fromUserId ?? data.FromUserId,
          isVideoOff: data.isVideoOff ?? data.IsVideoOff,
        });
        console.log('camera status changed', data.IsVideoOff);
      });

    this.signalR
      .on<any>(this.hubName, 'CallError')
      .pipe(takeUntil(this.destroy$))
      .subscribe((err: any) => {
        this.log('CallError event received', err);
        this.clearOutgoingCallTimeout();
        
        const errorCode = err?.errorCode ?? err?.ErrorCode ?? 'UNKNOWN_ERROR';
        
        if (this.currentCallId) {
          this.cleanupPeer();
        }

        this.callError$.next({
          errorCode: errorCode,
          message: err?.message ?? err?.Message,
        });
      });
  }

  private normalizeCallType(ct: any): number {
    if (typeof ct === 'number') return ct;
    const s = String(ct || '').toLowerCase();
    if (s === 'audio') return 1;
    if (s === 'video') return 2;
    return 2; // default to Video
  }

  private normalizeCallContextType(cct: any): number {
    if (typeof cct === 'number') return cct;
    const s = String(cct || '').toLowerCase();
    if (s === 'patient') return 1;
    if (s === 'consultation') return 2;
    return 1; // default to Patient
  }

  private clearOutgoingCallTimeout() {
    if (this.outgoingCallTimeoutTimer) {
      clearTimeout(this.outgoingCallTimeoutTimer);
      this.outgoingCallTimeoutTimer = undefined;
    }
  }

  private startOutgoingCallTimeout(callId: string) {
    this.clearOutgoingCallTimeout();

    this.outgoingCallTimeoutTimer = setTimeout(async () => {
      if (
        this.currentCallId === callId &&
        this.currentRole === 'caller' &&
        !this.pc?.remoteDescription
      ) {
        this.log('Outgoing call timed out (no response from callee)');

        try {
          await this.signalR.invoke(this.hubName, 'EndCall', {
            CallId: callId,
            Reason: 'NoResponse',
          });
        } catch (e) {
          this.log('EndCall after timeout failed', e);
        } finally {
          this.cleanupPeer();

          this.callRejected$.next({
            callId,
            reason: 'NoResponse',
          });

          this.callEnded$.next({
            callId,
            endedByUserId: 'system',
          });
        }
      }
    }, this.outgoingCallTimeoutMs);
  }

  async beginOutgoingCall(options: {
    calleeUserId?: string;
    patientId?: string;
    callType: any;
    video?: boolean;
    audio?: boolean;
    callContextType: any;
  }): Promise<{ callId: string }> {
    if (this.currentCallId || this.callState$.value.inCall) {
      this.callError$.next({
        errorCode: 'CALL_ALREADY_IN_ACTIVE_CALL',
      });
      
      throw new Error('CALL_ALREADY_IN_ACTIVE_CALL');
    }
    
    await this.ensureConnected();
    this.resetPerCallScope();
    this.currentRole = 'caller';

    const resp = await this.signalR.invoke(this.hubName, 'StartCall', {
      CalleeUserId: options.calleeUserId,
      PatientId: options.patientId,
      CallType: this.normalizeCallType(options.callType),
      CallContextType: this.normalizeCallContextType(options.callContextType),
    });

    const callId: string = resp?.callId ?? resp?.CallId ?? resp;
    
    if (!callId || typeof callId !== 'string') {
      throw new Error(resp?.message ?? resp?.Message ?? 'CALL_INIT_FAILED');
    }

    this.currentCallId = callId;

    this.callState$.next({
      inCall: true,
      callId,
      role: 'caller',
    });

    try {
      await this.setupPeerConnection(callId, options);
    } catch (error) {
      this.log('setupPeerConnection failed, cleaning up', error);
      this.cleanupPeer();
      throw error;
    }

    this.subscribeSignaling(callId, 'caller');
    this.startOutgoingCallTimeout(callId);

    return { callId };
  }

  async acceptIncomingCall(
    incoming: IncomingCallEvent,
    options?: { video?: boolean; audio?: boolean }
  ): Promise<void> {
    if (this.currentCallId && this.currentCallId !== incoming.callId) {
      this.log('acceptIncomingCall: Already in a different call', {
        currentCallId: this.currentCallId,
        incomingCallId: incoming.callId,
      });
      
      this.callError$.next({
        errorCode: 'CALL_ALREADY_IN_ACTIVE_CALL',
      });
      
      throw new Error('CALL_ALREADY_IN_ACTIVE_CALL');
    }
    
    await this.ensureConnected();
    this.resetPerCallScope();

    this.currentCallId = incoming.callId;
    this.currentRole = 'callee';

    await this.signalR.invoke(this.hubName, 'AcceptCall', {
      CallId: incoming.callId,
    });

    try {
      await this.setupPeerConnection(incoming.callId, {
        video: options?.video ?? true,
        audio: options?.audio ?? true,
      });
    } catch (error) {
      this.log('setupPeerConnection failed in acceptIncomingCall, cleaning up', error);
      this.cleanupPeer();
      
      try {
        await this.signalR.invoke(this.hubName, 'RejectCall', {
          CallId: incoming.callId,
          Reason: 'DeviceError',
        });
      } catch (rejectError) {
        this.log('Failed to reject call after device error', rejectError);
      }
      
      throw error;
    }

    try {
      const buffered = this.pendingOffers.get(incoming.callId);
      if (buffered?.sdp && this.pc) {
        await this.pc.setRemoteDescription({
          type: 'offer',
          sdp: buffered.sdp,
        });
        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);
        await this.signalR.invoke(this.hubName, 'SendAnswer', {
          CallId: incoming.callId,
          Sdp: answer.sdp,
          Type: 'answer',
        });
        this.log('Answer sent (from buffered offer)', {
          callId: incoming.callId,
        });
        this.pendingOffers.delete(incoming.callId);
      }
    } catch (e) {
      this.log('Apply buffered offer / send answer failed', e);
    }

    const cand = this.pendingCandidates.get(incoming.callId);
    if (cand?.length && this.pc) {
      for (const ic of cand) {
        try {
          await this.pc.addIceCandidate(ic);
        } catch (e) {
          console.warn('Buffered ICE add failed', e);
        }
      }
      this.pendingCandidates.delete(incoming.callId);
    }

    this.callState$.next({
      inCall: true,
      callId: incoming.callId,
      role: 'callee',
    });

    this.subscribeSignaling(incoming.callId, 'callee');
  }

  async rejectIncomingCall(callId: string, reason?: string): Promise<void> {
    await this.ensureConnected();
    await this.signalR.invoke(this.hubName, 'RejectCall', {
      CallId: callId,
      Reason: reason,
    });
  }

  async endCall(): Promise<void> {
    if (!this.currentCallId) return;
    if (
      this.currentCallId === null ||
      this.currentCallId === undefined ||
      this.currentCallId === ''
    ) {
      return;
    }
    
    const callIdToEnd = this.currentCallId;
    
    try {
      await this.signalR.invoke(this.hubName, 'EndCall', {
        CallId: callIdToEnd,
      });
    } catch (e) {
      this.log('EndCall invoke failed:', e);
    } finally {
      this.cleanupPeer();
      this.callState$.next({
        inCall: false,
      });
    }
  }

  private subscribeSignaling(callId: string, role: CallRole) {
    const kill$ = this.perCallTeardown$!;

    if (role === 'caller') {
      const buf = this.pendingAnswers.get(callId);
      if (buf?.sdp) {
        (async () => {
          try {
            if (this.pc && !this.pc.remoteDescription) {
              await this.pc.setRemoteDescription({
                type: 'answer',
                sdp: buf.sdp,
              });
              this.log('Applied buffered Answer before stream', { callId });
              this.clearAnswerWatch();
            }
          } catch (e) {
            this.log('Failed to apply buffered Answer', e);
          } finally {
            this.pendingAnswers.delete(callId);
          }
        })();
      }
    }

    this.signalR
      .on<{ CallId: string; Sdp: string }>(this.hubName, 'ReceiveAnswer')
      .pipe(
        takeUntil(kill$),
        takeUntil(this.destroy$),
        filter((ans: any) => (ans.callId ?? ans.CallId) === callId)
      )
      .subscribe(async (ans: any) => {
        try {
          if (!this.pc) return;
          const remoteSdp = ans.sdp ?? ans.Sdp;
          if (this.pc.remoteDescription?.type === 'answer') {
            this.log('Answer already applied, ignoring duplicate');
            return;
          }
          await this.pc.setRemoteDescription({
            type: 'answer',
            sdp: remoteSdp,
          });
          this.clearOutgoingCallTimeout();
          this.log('Answer applied (live)');
          this.clearAnswerWatch();

          const cand = this.pendingCandidates.get(callId);
          if (cand?.length) {
            for (const ic of cand) {
              try {
                await this.pc.addIceCandidate(ic);
              } catch (e) {
                console.warn('Buffered ICE add failed (caller)', e);
              }
            }
            this.pendingCandidates.delete(callId);
          }
        } catch (e) {
          this.log('Apply live Answer failed', e);
        }
      });

    if (role === 'callee') {
      this.signalR
        .on<{ CallId: string; Sdp: string }>(this.hubName, 'ReceiveOffer')
        .pipe(
          takeUntil(kill$),
          takeUntil(this.destroy$),
          filter((off: any) => (off.callId ?? off.CallId) === callId)
        )
        .subscribe(async (off: any) => {
          if (!this.pc) return;
          const remoteSdp = off.sdp ?? off.Sdp;
          await this.pc.setRemoteDescription({
            type: 'offer',
            sdp: remoteSdp,
          });

          const answer = await this.pc.createAnswer();
          await this.pc.setLocalDescription(answer);
          await this.signalR.invoke(this.hubName, 'SendAnswer', {
            CallId: callId,
            Sdp: answer.sdp,
            Type: 'answer',
          });

          this.pendingOffers.delete(callId);

          const cand = this.pendingCandidates.get(callId);
          if (cand?.length) {
            for (const ic of cand) {
              try {
                await this.pc.addIceCandidate(ic);
              } catch (e) {
                console.warn('Buffered ICE add failed', e);
              }
            }
            this.pendingCandidates.delete(callId);
          }
        });
    }

    this.signalR
      .on<any>(this.hubName, 'ReceiveIceCandidate')
      .pipe(
        takeUntil(kill$),
        takeUntil(this.destroy$),
        filter((c: any) => (c.callId ?? c.CallId) === callId)
      )
      .subscribe(async (c: any) => {
        if (!this.pc) return;
        const candStr = c.candidate ?? c.Candidate;
        const info = this.parseCandInfo(candStr);
        this.candRecv++;
        this.log(
          'recv ICE',
          info,
          'remoteDescSet=',
          !!this.pc.remoteDescription
        );
        const ic: RTCIceCandidateInit = {
          candidate: candStr,
          sdpMid: c.sdpMid ?? c.SdpMid,
          sdpMLineIndex: c.sdpMLineIndex ?? c.SdpMLineIndex,
        };
        if (!this.pc.remoteDescription) {
          const list = this.pendingCandidates.get(callId) ?? [];
          list.push(ic);
          this.pendingCandidates.set(callId, list);
          return;
        }
        try {
          await this.pc.addIceCandidate(ic);
        } catch (err) {
          console.error('Failed to add ICE candidate', err);
        }
      });
  }

  // --- WebRTC setup ---
  private parseCandInfo(cand?: string) {
    if (!cand) return {};
    const mType = cand.match(/ typ (\w+)/);
    const mTcp = cand.match(/ tcp /);
    return { raw: cand, type: mType?.[1], transport: mTcp ? 'tcp' : 'udp' };
  }

  // Optionally load ICE/TURN config from backend once
  private async loadIceConfig(): Promise<void> {
    if (this.iceServersLoaded) return;
    this.iceServersLoaded = true;
    const path = environment.webrtc?.iceConfigPath;
    if (!path) return;
    try {
      let url: string = environment.apiLocations.communication + path;

      const resp: any = await firstValueFrom(this.apiService.get(url));
      // Accept common shapes: { iceServers: RTCIceServer[] } or plain array
      const servers = Array.isArray(resp) ? resp : resp?.iceServers;
      if (Array.isArray(servers) && servers.length) {
        this.dynamicIceServers = servers as RTCIceServer[];
        this.log(
          'Loaded ICE servers from backend',
          servers.map((s: any) => s.urls)
        );
      } else {
        this.log(
          'ICE config endpoint returned no servers, using environment defaults'
        );
      }
    } catch (e) {
      this.log(
        'Failed to load ICE config from backend, using environment defaults',
        e
      );
    }
  }

  private getRtcConfiguration(): RTCConfiguration {
    const envServers = environment.webrtc?.iceServers ?? [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun.services.mozilla.com' },
    ];
    const servers = this.dynamicIceServers ?? envServers;
    const hasTurn = (servers || []).some((s: any) => {
      const urls = Array.isArray(s.urls) ? s.urls : [s.urls];
      return urls?.some((u: any) => String(u).startsWith('turn'));
    });

    let policy = environment.webrtc?.iceTransportPolicy;
    if (policy === 'relay' && !hasTurn) {
      policy = 'all';
      this.log(
        'No TURN servers configured; overriding iceTransportPolicy to "all".'
      );
    }

    const cfg: RTCConfiguration = {
      iceServers: servers,
      iceTransportPolicy: policy,
      bundlePolicy: 'max-bundle' as any,
    };
    this.log('RTCConfiguration', {
      policy: cfg.iceTransportPolicy ?? 'all',
      iceServers: (cfg.iceServers || []).map((s: any) => ({ urls: s.urls })),
    });
    return cfg;
  }

  private async setupPeerConnection(
    callId: string,
    media?: { video?: boolean; audio?: boolean }
  ): Promise<void> {
    const constraints = {
      video: media?.video ?? true,
      audio: media?.audio ?? true,
    };
    
    // FIX #1: Add comprehensive media permission error handling
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia(constraints);
      this.localStream$.next(this.localStream);
      this.log('gotUserMedia', constraints, {
        audio: this.localStream.getAudioTracks().length,
        video: this.localStream.getVideoTracks().length,
      });
    } catch (error: any) {
      this.log('getUserMedia failed', error);
      
      let errorCode = 'MEDIA_DEVICE_ERROR';
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorCode = 'MEDIA_PERMISSION_DENIED';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorCode = 'MEDIA_DEVICE_NOT_FOUND';
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        errorCode = 'MEDIA_DEVICE_IN_USE';
      } else if (error.name === 'OverconstrainedError' || error.name === 'ConstraintNotSatisfiedError') {
        errorCode = 'MEDIA_CONSTRAINTS_ERROR';
      } else if (error.name === 'TypeError') {
        errorCode = 'MEDIA_CONSTRAINTS_INVALID';
      } else if (error.name === 'AbortError') {
        errorCode = 'MEDIA_DEVICE_ABORTED';
      }
      
      this.callError$.next({ errorCode });
      
      this.currentCallId = undefined;
      this.currentRole = undefined;
      this.callState$.next({ inCall: false });
      
      throw new Error(errorCode);
    }

    await this.loadIceConfig();

    this.pc = new RTCPeerConnection(this.getRtcConfiguration());

    this.candSent = 0;
    this.candRecv = 0;

    this.pc.onicegatheringstatechange = () => {
      this.log('iceGatheringState', this.pc?.iceGatheringState);
      if (this.pc?.iceGatheringState === 'complete') {
        this.log('ICE gathering complete', {
          sent: this.candSent,
          received: this.candRecv,
        });
        if (
          environment.webrtc?.iceTransportPolicy === 'relay' &&
          this.candSent === 0
        ) {
          this.log(
            'Warning: No local relay candidates gathered with policy "relay". TURN likely unreachable/invalid.'
          );
        }
      }
    };

    this.pc.oniceconnectionstatechange = () => {
      const state = this.pc?.iceConnectionState;
      this.log('iceConnectionState', state);
      if (
        state === 'disconnected' ||
        state === 'failed' ||
        state === 'closed'
      ) {
        this.scheduleRemoteDisconnectCheck();
      } else if (state === 'connected' || state === 'completed') {
        this.clearRemoteDisconnectCheck();
      }
    };
    this.pc.onconnectionstatechange = () =>
      this.log('connectionState', this.pc?.connectionState);
    this.pc.onnegotiationneeded = () => this.log('negotiationneeded');

    this.pc.onicecandidateerror = (e: any) => {
      this.log('onicecandidateerror', {
        url: e?.url,
        errorCode: e?.errorCode,
        errorText: e?.errorText,
      });
    };

    this.pc.onicecandidate = (e) => {
      if (!e.candidate || !this.currentCallId) return;
      const info = this.parseCandInfo(e.candidate.candidate);
      this.candSent++;
      this.log('send ICE', info);
      this.signalR.invoke(this.hubName, 'SendIceCandidate', {
        CallId: this.currentCallId,
        Candidate: e.candidate.candidate,
        SdpMid: e.candidate.sdpMid!,
        SdpMLineIndex: e.candidate.sdpMLineIndex!,
      });
    };

    const remote = new MediaStream();
    this.remoteStream$.next(remote);
    this.pc.ontrack = (ev) => {
      this.log('ontrack', {
        streams: ev.streams?.length,
        kind: ev.track?.kind,
      });
      if (ev.streams && ev.streams[0]) {
        ev.streams[0].getTracks().forEach((t) => remote.addTrack(t));
      } else if (ev.track) {
        remote.addTrack(ev.track);
      }
    };

    this.localStream
      .getTracks()
      .forEach((t) => this.pc!.addTrack(t, this.localStream!));

    setTimeout(() => this.dumpSelectedCandidatePair(), 4000);

    this.watchIceConnectivity(12000);
  }

  private answerTimer?: any;
  private scheduleAnswerWatch(callId: string) {
    this.clearAnswerWatch();
    this.answerTimer = setTimeout(async () => {
      try {
        if (!this.pc || this.pc.remoteDescription) return;
        this.log('No Answer yet, re-sending offer...', { callId });
        const offer = await this.pc.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: true,
          iceRestart: false,
        });
        await this.pc.setLocalDescription(offer);
        await this.signalR.invoke(this.hubName, 'SendOffer', {
          CallId: callId,
          Sdp: offer.sdp,
          Type: 'offer',
        });
      } catch (e) {
        this.log('Offer re-send failed', e);
      }
    }, 3500);
  }
  private clearAnswerWatch() {
    if (this.answerTimer) {
      clearTimeout(this.answerTimer);
      this.answerTimer = undefined;
    }
  }

  private async watchIceConnectivity(timeoutMs: number) {
    const start = Date.now();
    const tick = async () => {
      if (!this.pc) return;
      const state = this.pc.iceConnectionState;
      this.log('ICE check', state);
      if (state === 'connected' || state === 'completed') {
        this.dumpSelectedCandidatePair?.();
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        this.dumpSelectedCandidatePair?.();
        this.log('ICE timeout', {
          sent: this.candSent,
          received: this.candRecv,
          policy: environment.webrtc?.iceTransportPolicy ?? 'all',
        });
        // Try a one-shot ICE restart from caller only
        try {
          if (
            this.currentRole === 'caller' &&
            this.pc?.signalingState === 'stable'
          ) {
            this.log('Attempting ICE restart (caller)');
            const offer = await this.pc.createOffer({
              iceRestart: true,
              offerToReceiveAudio: true,
              offerToReceiveVideo: true,
            });
            await this.pc.setLocalDescription(offer);
            await this.signalR.invoke(this.hubName, 'SendOffer', {
              CallId: this.currentCallId,
              Sdp: offer.sdp,
              Type: 'offer',
            });
          }
        } catch (e) {
          this.log('ICE restart failed', e);
        }
        return;
      }
      setTimeout(tick, 1000);
    };
    setTimeout(tick, 1000);
  }

  private async dumpSelectedCandidatePair() {
    if (!this.pc) return;
    try {
      const stats = await this.pc.getStats();

      let selectedPairId: string | undefined;
      const candidatePairs: any[] = [];
      const localCandidates: Record<string, any> = {};
      const remoteCandidates: Record<string, any> = {};

      // Iterate safely without relying on Map.prototype.values()
      stats.forEach((r: any) => {
        if (r.type === 'transport' || r.type === 'rtp-transport') {
          if (r.selectedCandidatePairId)
            selectedPairId = r.selectedCandidatePairId;
        } else if (r.type === 'candidate-pair') {
          candidatePairs.push(r);
          if (r.nominated && r.state === 'succeeded') selectedPairId = r.id;
        } else if (r.type === 'local-candidate') {
          localCandidates[r.id] = r;
        } else if (r.type === 'remote-candidate') {
          remoteCandidates[r.id] = r;
        }
      });

      // Resolve selected pair
      let pair = candidatePairs.find((p) => p.id === selectedPairId);
      if (!pair) {
        pair = candidatePairs.find(
          (p) => p.nominated && p.state === 'succeeded'
        );
      }

      if (pair) {
        const local = localCandidates[pair.localCandidateId];
        const remote = remoteCandidates[pair.remoteCandidateId];
        this.log('Selected candidate pair', {
          pairState: pair?.state,
          local: {
            type: local?.candidateType,
            proto: local?.protocol,
            ip: local?.ip ?? local?.address,
            port: local?.port,
          },
          remote: {
            type: remote?.candidateType,
            proto: remote?.protocol,
            ip: remote?.ip ?? remote?.address,
            port: remote?.port,
          },
        });
      } else {
        this.log('No selected candidate pair yet');
      }
    } catch (e) {
      this.log('dumpSelectedCandidatePair error', e);
    }
  }

  private scheduleRemoteDisconnectCheck() {
    this.clearRemoteDisconnectCheck();
    this.remoteDisconnectTimer = setTimeout(async () => {
      const endedCallId = this.currentCallId;
      const ice = this.pc?.iceConnectionState;
      if (ice !== 'connected' && ice !== 'completed') {
        this.log('Remote peer likely left (refresh/close). Auto-ending call.');
        try {
          await this.endCall();
        } catch {
          this.cleanupPeer();
        } finally {
          if (endedCallId) {
            this.callEnded$.next({
              callId: endedCallId,
              endedByUserId: 'remote' as any,
            });
          } else {
            this.callEnded$.next({
              callId: '',
              endedByUserId: 'remote' as any,
            });
          }
        }
      }
    }, this.remoteDisconnectGraceMs);
  }

  private clearRemoteDisconnectCheck() {
    if (this.remoteDisconnectTimer) {
      clearTimeout(this.remoteDisconnectTimer);
      this.remoteDisconnectTimer = undefined;
    }
  }

  muteAudio(muted: boolean) {
    this.localStream?.getAudioTracks().forEach((t) => (t.enabled = !muted));
  }

  disableVideo(disabled: boolean) {
    this.localStream?.getVideoTracks().forEach((t) => (t.enabled = !disabled));
  }

  private cleanupPeer() {
    this.log('cleanupPeer: Starting cleanup');
    
    this.clearOutgoingCallTimeout();
    this.clearAnswerWatch();
    this.clearRemoteDisconnectCheck();
    
    const endedCallId = this.currentCallId;
    
    this.currentCallId = undefined;
    
    this.currentRole = undefined;
    
    if (this.perCallTeardown$) {
      try {
        this.perCallTeardown$.next();
        this.perCallTeardown$.complete();
      } catch (e) {
        this.log('Error completing perCallTeardown$', e);
      }
      this.perCallTeardown$ = undefined;
    }
    
    try {
      if (this.pc) {
        this.pc.getSenders().forEach((s) => {
          try {
            if (s.track) s.track.stop();
          } catch (e) {
            this.log('Error stopping sender track', e);
          }
        });
        
        this.pc.close();
      }
    } catch (e) {
      this.log('Error closing peer connection', e);
    }
    this.pc = undefined;
    
    if (this.localStream) {
      try {
        this.localStream.getTracks().forEach((t) => {
          try {
            t.stop();
          } catch (e) {
            this.log('Error stopping local track', e);
          }
        });
      } catch (e) {
        this.log('Error stopping local stream tracks', e);
      }
    }
    this.localStream = undefined;
    this.localStream$.next(null);
    this.remoteStream$.next(null);

    this.pendingOffers.clear();
    this.pendingCandidates.clear();
    this.pendingAnswers.clear();
    
    this.recentIncoming.clear();
    
    this.candSent = 0;
    this.candRecv = 0;

    this.callState$.next({
      inCall: false,
    });
    
    this.log('cleanupPeer: Cleanup complete', { endedCallId });
  }

  private resetPerCallScope() {
    if (this.perCallTeardown$) {
      this.perCallTeardown$.next();
      this.perCallTeardown$.complete();
    }
    this.perCallTeardown$ = new Subject<void>();
  }

  private log(...args: any[]) {
    console.log('[VideoChat]', ...args);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupPeer();
  }
}
